/**
 * EPI Form Wizard Types
 * Types for the multi-step EPI form wizard
 *
 * @version 2025.1.0
 */

import { Epi, EpiFormData } from '../../../../types/epi';

/**
 * Wizard step identifiers
 */
export type EPIWizardStep = 'identification' | 'certificate' | 'stock';

/**
 * Step configuration
 */
export interface StepConfig {
  key: EPIWizardStep;
  label: string;
  optional: boolean;
}

/**
 * All wizard steps configuration
 */
export const EPI_WIZARD_STEPS: StepConfig[] = [
  { key: 'identification', label: 'Identificação', optional: false },
  { key: 'certificate', label: 'C.A.', optional: false },
  { key: 'stock', label: 'Estoque', optional: true },
];

/**
 * Props for EPIFormWizard component
 */
export interface EPIFormWizardProps {
  /** Initial data for editing */
  initialData?: Partial<Epi>;
  /** Callback when form is submitted */
  onSubmit: (data: EpiFormData) => Promise<void>;
  /** Callback when wizard is cancelled */
  onCancel: () => void;
  /** Whether form is submitting */
  isSubmitting?: boolean;
}

/**
 * Wizard internal state
 */
export interface EPIWizardState {
  /** Current step index */
  activeStep: number;
  /** Form data collected across steps */
  formData: Partial<EpiFormData>;
  /** Loading state during validation */
  loading: boolean;
  /** Error message if operation failed */
  error: string | null;
}

/**
 * Props for step components
 */
export interface StepProps {
  disabled?: boolean;
}

/**
 * Props for IdentificationStep
 */
export interface IdentificationStepProps extends StepProps {}

/**
 * Props for CertificateStep
 */
export interface CertificateStepProps extends StepProps {}

/**
 * Props for StockStep
 */
export interface StockStepProps extends StepProps {}

/**
 * Return type for useEPIFormWizard hook
 */
export interface UseEPIFormWizardReturn {
  // State
  state: EPIWizardState;
  steps: StepConfig[];

  // Step navigation
  isFirstStep: boolean;
  isLastStep: boolean;
  currentStepKey: EPIWizardStep;
  canSkip: boolean;

  // Actions
  handleNext: () => Promise<boolean>;
  handleBack: () => void;
  handleSkip: () => void;
  setError: (error: string | null) => void;
  reset: () => void;
}
